<template>
  <div class="webview-page">
    <!-- 顶部栏：返回（回到商城聊天页 / 上一页）+ 域名标题 -->
    <van-nav-bar
      :title="navTitle"
      left-text="返回"
      left-arrow
      fixed
      @click-left="goBack"
    />

    <div class="frame-box">
      <!-- 无效地址 -->
      <div v-if="!url" class="state-box">
        <van-icon name="warning-o" size="46" color="#ff6b35" />
        <p>链接地址无效，无法打开</p>
        <van-button type="primary" round size="small" @click="goBack">返回商城</van-button>
      </div>

      <template v-else>
        <iframe
          ref="frame"
          class="frame"
          :src="url"
          frameborder="0"
          @load="onFrameLoad"
          @error="onFrameError"
        ></iframe>

        <!-- 加载中 -->
        <div v-if="!loaded && !failed" class="state-box loading-box">
          <van-loading color="#ff6b35" />
          <p>正在加载网页...</p>
        </div>

        <!-- 无法在页面内打开（被目标网站禁止嵌入 / http 混合内容 / 加载超时） -->
        <div v-if="failed" class="state-box fail-box">
          <van-icon name="info-o" size="46" color="#ff6b35" />
          <p>{{ failTip }}</p>
          <div class="fail-actions">
            <van-button type="primary" round size="small" @click="openInSystemBrowser">浏览器打开</van-button>
            <van-button plain type="primary" round size="small" @click="copyLink">复制链接</van-button>
          </div>
        </div>
      </template>
    </div>

    <!-- 底部常驻操作条：始终保留一条「回到商城」的出口，页面空白也能安全返回 -->
    <div class="webview-bar">
      <span class="bar-btn" @click="goBack">
        <van-icon name="arrow-left" /> 返回商城
      </span>
      <span class="bar-host">{{ host }}</span>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { copyText } from '@/utils/clipboard'
import { isExternalHttpUrl, getUrlHost } from '@/utils/link'

// 目标网站禁止被 iframe 嵌入时，各浏览器不一定触发 load 失败事件，
// 这里在超时后主动给出「浏览器打开 / 复制链接」的兜底提示（正常情况下页面只有返回入口）
const LOAD_TIMEOUT = 8000

export default {
  name: 'WebView',
  data () {
    return {
      url: '',
      host: '',
      loaded: false,
      failed: false,
      failTip: '该网页不支持在商城页面内打开，请使用浏览器打开',
      loadTimer: null
    }
  },
  computed: {
    navTitle () {
      return this.$route.query.title || this.host || '网页'
    }
  },
  created () {
    // 仅允许 http / https 地址（防止 javascript: 等危险协议）
    const raw = String(this.$route.query.url || '').trim()
    this.url = raw && isExternalHttpUrl(raw) ? raw : ''
    this.host = this.url ? getUrlHost(this.url) : ''
    // https 页面中嵌入 http 网页会被浏览器拦截（混合内容），直接给出兜底提示
    if (this.url && window.location.protocol === 'https:' && /^http:\/\//i.test(this.url)) {
      this.failed = true
      this.failTip = '该链接为 http 地址，在 https 页面上无法内嵌打开，请使用浏览器打开'
    }
  },
  mounted () {
    if (!this.url || this.failed) return
    this.loadTimer = setTimeout(() => {
      this.loadTimer = null
      if (!this.loaded) this.failed = true
    }, LOAD_TIMEOUT)
  },
  beforeDestroy () {
    this.clearLoadTimer()
  },
  methods: {
    clearLoadTimer () {
      if (this.loadTimer) {
        clearTimeout(this.loadTimer)
        this.loadTimer = null
      }
    },
    onFrameLoad () {
      this.loaded = true
      this.failed = false
      this.clearLoadTimer()
    },
    onFrameError () {
      this.failed = true
      this.clearLoadTimer()
    },
    // 返回：优先回到上一页（通常是商城聊天页），无历史记录时回商城首页
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/home')
      }
    },
    // 用系统 / 外部浏览器打开（仅「无法在页面内打开」的兜底提示里使用；
    // 微信、QQ 内置浏览器拦截新窗口时回退为当前页跳转）
    openInSystemBrowser () {
      if (!this.url) return
      const win = window.open(this.url, '_blank')
      if (!win) window.location.href = this.url
    },
    async copyLink () {
      if (!this.url) return
      const ok = await copyText(this.url)
      Toast(ok ? '链接已复制' : '复制失败，请长按链接手动复制')
    }
  }
}
</script>

<style lang="less" scoped>
.webview-page {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  padding-top: 46px;
  background: #fff;

  /deep/ .van-nav-bar__arrow { color: #333; }
  /deep/ .van-nav-bar__text { color: #333; }

  .frame-box {
    flex: 1;
    position: relative;
    overflow: hidden;

    .frame {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 0;
      background: #fff;
    }

    .state-box {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 0 32px;
      text-align: center;
      background: #f7f8fa;

      p {
        margin: 14px 0 20px;
        color: #666;
        font-size: 14px;
        line-height: 20px;
      }

      .fail-actions {
        display: flex;
        align-items: center;

        /deep/ .van-button + .van-button {
          margin-left: 12px;
        }
      }
    }
  }

  .webview-bar {
    display: flex;
    align-items: center;
    height: 46px;
    padding: 0 12px;
    padding-bottom: env(safe-area-inset-bottom);
    background: #fafafa;
    border-top: 1px solid #ebedf0;
    font-size: 13px;
    color: #666;

    .bar-btn {
      display: inline-flex;
      align-items: center;
      color: #1989fa;
      cursor: pointer;
      flex-shrink: 0;

      /deep/ .van-icon {
        margin-right: 2px;
      }
    }

    .bar-host {
      flex: 1;
      min-width: 0;
      margin: 0 10px;
      text-align: center;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      color: #999;
    }
  }
}
</style>
